import type {
  AdminComment,
  BillingPeriod,
  CommentFilterMode,
  ContentRating,
  Game,
  NotificationKind,
  NotificationTopic,
  Product,
  Role,
  Submission,
  SubmissionStatus,
  Subscription,
  TierId,
  User,
} from '@gobingebox/shared';
import { apiFetch } from '@/lib/api';

export interface CommentShow {
  id: string;
  title: string;
  commentsEnabled: boolean;
  commentFilter: boolean;
}

export interface GameInput {
  title?: string;
  description?: string;
  embedUrl?: string | null;
  thumbnailUrl?: string | null;
  rating?: ContentRating | null;
  active?: boolean;
}
export interface ProductInput {
  name?: string;
  description?: string;
  priceCents?: number;
  images?: string[];
  stock?: number;
  active?: boolean;
}

export interface AdminUser extends User {
  subscription: Subscription | null;
  banned: boolean;
  bannedReason: string | null;
}

export interface AdminOverview {
  revenue: { netCents: number; grossCents: number; refundedCents: number; monthNetCents: number; payments: number };
  users: { total: number; new30d: number; banned: number };
  subscriptions: { active: number; paying: number; comp: number };
  comments: { total: number; held: number };
  openSubmissions: number;
}

export const adminApi = {
  users: (q?: string) =>
    apiFetch<{ users: AdminUser[] }>(`/admin/users${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  setRole: (id: string, role: Role) =>
    apiFetch<{ ok: boolean; user: User }>(`/admin/users/${id}/role`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    }),
  grantComp: (id: string, input: { tier: TierId; streams: number; billingPeriod?: BillingPeriod }) =>
    apiFetch<{ ok: boolean; subscription: Subscription }>(`/admin/users/${id}/comp`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  revokeComp: (id: string) =>
    apiFetch<{ ok: boolean }>(`/admin/users/${id}/comp`, { method: 'DELETE' }),

  overview: () => apiFetch<AdminOverview>('/admin/overview'),
  ban: (id: string, reason?: string) =>
    apiFetch<{ ok: boolean }>(`/admin/users/${id}/ban`, { method: 'POST', body: JSON.stringify({ reason }) }),
  unban: (id: string) => apiFetch<{ ok: boolean }>(`/admin/users/${id}/ban`, { method: 'DELETE' }),

  comments: (status?: 'held') =>
    apiFetch<{ comments: AdminComment[] }>(`/admin/comments${status ? `?status=${status}` : ''}`),
  approveComment: (id: string) =>
    apiFetch<{ ok: boolean }>(`/admin/comments/${id}/approve`, { method: 'POST', body: JSON.stringify({}) }),
  deleteComment: (id: string) => apiFetch<{ ok: boolean }>(`/admin/comments/${id}`, { method: 'DELETE' }),
  commentSettings: () =>
    apiFetch<{ mode: CommentFilterMode; blockedWords: string; defaultWords: string }>('/admin/comment-settings'),
  setCommentSettings: (mode: CommentFilterMode, blockedWords: string) =>
    apiFetch<{ ok: boolean }>('/admin/comment-settings', {
      method: 'PUT',
      body: JSON.stringify({ mode, blockedWords }),
    }),
  commentShows: () => apiFetch<{ shows: CommentShow[] }>('/admin/comment-shows'),
  setShowComments: (id: string, input: { commentsEnabled?: boolean; commentFilter?: boolean }) =>
    apiFetch<{ ok: boolean }>(`/admin/shows/${id}/comments`, { method: 'PATCH', body: JSON.stringify(input) }),

  submissions: (status?: SubmissionStatus) =>
    apiFetch<{ submissions: Submission[] }>(
      `/admin/submissions${status ? `?status=${status}` : ''}`,
    ),
  setSubmissionStatus: (id: string, status: SubmissionStatus) =>
    apiFetch<{ ok: boolean }>(`/admin/submissions/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),
  getSettings: () =>
    apiFetch<{ devPortalOpen: boolean; devPortalMessage: string | null }>('/admin/settings'),
  setDevPortal: (open: boolean, message?: string) =>
    apiFetch<{ ok: boolean }>('/admin/settings/dev-portal', {
      method: 'PUT',
      body: JSON.stringify({ open, message }),
    }),

  notificationCounts: () =>
    apiFetch<{ counts: Record<NotificationTopic, number> }>('/admin/notifications'),
  sendNotification: (topic: NotificationTopic, subject: string, message: string) =>
    apiFetch<{ ok: boolean; recipients: number; sent: number }>('/admin/notifications/send', {
      method: 'POST',
      body: JSON.stringify({ topic, subject, message }),
    }),
  sendInApp: (input: { kind: NotificationKind; title: string; body?: string; link?: string }) =>
    apiFetch<{ ok: boolean; sent: number }>('/admin/notifications/in-app', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  games: () => apiFetch<{ games: Game[] }>('/admin/games'),
  createGame: (input: GameInput) =>
    apiFetch<{ game: Game }>('/admin/games', { method: 'POST', body: JSON.stringify(input) }),
  updateGame: (id: string, input: GameInput) =>
    apiFetch<{ ok: boolean }>(`/admin/games/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
  deleteGame: (id: string) => apiFetch<{ ok: boolean }>(`/admin/games/${id}`, { method: 'DELETE' }),

  products: () => apiFetch<{ products: Product[] }>('/admin/products'),
  createProduct: (input: ProductInput) =>
    apiFetch<{ product: Product }>('/admin/products', { method: 'POST', body: JSON.stringify(input) }),
  updateProduct: (id: string, input: ProductInput) =>
    apiFetch<{ ok: boolean }>(`/admin/products/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
  deleteProduct: (id: string) =>
    apiFetch<{ ok: boolean }>(`/admin/products/${id}`, { method: 'DELETE' }),
};
